import Cargando from "../components/Cargando";
import React, { useState, useMemo } from "react";
import * as XLSX from "xlsx";
import { useProveedores } from "../hooks/useProveedores";
import { INICIAL } from "../components/proveedores/constants";
import FormProveedor from "../components/proveedores/FormProveedor";
import Modal360      from "../components/proveedores/Modal360";
import ProveedorCard from "../components/proveedores/ProveedorCard";

export default function Proveedores() {
  const { proveedores, loading, crear, actualizar, eliminar } = useProveedores("materiales");

  const [form,       setForm]       = useState(INICIAL);
  const [mostrar,    setMostrar]    = useState(false);
  const [editId,     setEditId]     = useState(null);
  const [busqueda,   setBusqueda]   = useState("");
  const [filtroCats, setFiltroCats] = useState([]);
  const [filtroCal,  setFiltroCal]  = useState("Todos");
  const [perfil360,  setPerfil360]  = useState(null);

  const catsEnUso = useMemo(() => {
    const set = new Set();
    proveedores.forEach(p => {
      (p.categorias?.length > 0 ? p.categorias : (p.materiales ? p.materiales.split(",").map(m => m.trim()) : []))
        .filter(Boolean).forEach(c => set.add(c));
    });
    return [...set].sort();
  }, [proveedores]);

  const toggleFiltroCat = (cat) =>
    setFiltroCats(prev => prev.includes(cat) ? prev.filter(c => c !== cat) : [...prev, cat]);

  const filtrados = useMemo(() => {
    let lista = [...proveedores];
    if (busqueda) {
      const b = busqueda.toLowerCase();
      lista = lista.filter(p =>
        p.nombreComercial?.toLowerCase().includes(b) ||
        p.nombre?.toLowerCase().includes(b) ||
        p.ruc?.includes(b) ||
        p.ciudad?.toLowerCase().includes(b) ||
        p.categorias?.some(c => c.toLowerCase().includes(b)) ||
        p.materiales?.toLowerCase().includes(b) ||
        p.contactos?.some(c => c.nombre?.toLowerCase().includes(b) || c.zona?.toLowerCase().includes(b)) ||
        p.contacto?.toLowerCase().includes(b) ||
        p.notas?.toLowerCase().includes(b)
      );
    }
    if (filtroCats.length > 0) {
      lista = lista.filter(p => {
        const cats = p.categorias?.length > 0 ? p.categorias
          : (p.materiales ? p.materiales.split(",").map(m => m.trim()) : []);
        return filtroCats.every(fc => cats.includes(fc));
      });
    }
    if (filtroCal !== "Todos") lista = lista.filter(p => String(p.calificacion) === filtroCal);
    return lista;
  }, [proveedores, busqueda, filtroCats, filtroCal]);

  const kpis = useMemo(() => ({
    total:      proveedores.length,
    conCredito: proveedores.filter(p => Number(p.creditoDias) > 0).length,
    calProm:    proveedores.length > 0
      ? (proveedores.reduce((s, p) => s + (Number(p.calificacion) || 5), 0) / proveedores.length).toFixed(1)
      : 0,
    estrellas5: proveedores.filter(p => Number(p.calificacion) >= 5).length,
  }), [proveedores]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (editId) await actualizar(editId, form);
    else await crear(form);
    setForm(INICIAL); setEditId(null); setMostrar(false);
  };

  const editar = (p) => {
    setForm({
      ...INICIAL, ...p,
      nombreComercial: p.nombreComercial || p.nombre || "",
      nombre: p.nombre || "",
      categorias: p.categorias?.length > 0 ? p.categorias
        : (p.materiales ? p.materiales.split(",").map(m => m.trim()).filter(Boolean) : []),
      contactos: p.contactos?.length > 0 ? p.contactos
        : (p.contacto ? [{ nombre: p.contacto, cargo: "", zona: "", telefono: p.telefono || "", email: p.email || "" }] : []),
    });
    setEditId(p.id);
    setMostrar(true);
    setPerfil360(null);
  };

  const exportarExcel = () => {
    const data = filtrados.map(p => ({
      "Nombre comercial": p.nombreComercial || p.nombre || "",
      "Razón social":     p.nombre || "",
      "RUC":              p.ruc || "",
      "Ciudad":           p.ciudad || "",
      "Dirección":        p.direccion || "",
      "Categorías":       (p.categorias?.length > 0 ? p.categorias : (p.materiales ? [p.materiales] : [])).join(", "),
      "Contacto 1":       p.contactos?.[0]?.nombre || p.contacto || "",
      "Cargo 1":          p.contactos?.[0]?.cargo || "",
      "Zona 1":           p.contactos?.[0]?.zona || "",
      "Teléfono 1":       p.contactos?.[0]?.telefono || p.telefono || "",
      "Contacto 2":       p.contactos?.[1]?.nombre || "",
      "Zona 2":           p.contactos?.[1]?.zona || "",
      "Plazo (d)":        p.plazoDias || "",
      "Crédito (d)":      p.creditoDias || "",
      "Calificación":     p.calificacion || "",
      "Notas":            p.notas || "",
    }));
    const ws = XLSX.utils.json_to_sheet(data);
    ws["!cols"] = Array(15).fill({ wch: 18 });
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Proveedores");
    XLSX.writeFile(wb, `Proveedores_${new Date().toLocaleDateString("es-PE").replace(/\//g,"-")}.xlsx`);
  };

  if (loading) return <Cargando />;

  const hayFiltros = busqueda || filtroCats.length > 0 || filtroCal !== "Todos";

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>

      {perfil360 && (
        <Modal360
          proveedor={perfil360}
          onClose={() => setPerfil360(null)}
          onEditar={() => editar(perfil360)}
        />
      )}

      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 10 }}>
        <h1 style={{ fontSize: 22, fontWeight: 700, color: "#1e293b" }}>
          Proveedores <span style={{ color: "#94a3b8", fontWeight: 400, fontSize: 16 }}>({filtrados.length})</span>
        </h1>
        <div style={{ display: "flex", gap: 8 }}>
          <button onClick={exportarExcel} style={{ background: "#fff", color: "#1D9E75", border: "1.5px solid #1D9E75", fontSize: 13, fontWeight: 600, padding: "8px 14px", borderRadius: 8, cursor: "pointer" }}>
            ↓ Excel
          </button>
          <button onClick={() => { setMostrar(!mostrar); setEditId(null); setForm(INICIAL); }}
            style={{ background: "#1e3a5f", color: "#fff", border: "none", borderRadius: 8, padding: "8px 18px", fontSize: 13, fontWeight: 600, cursor: "pointer" }}>
            {mostrar ? "Cancelar" : "+ Agregar proveedor"}
          </button>
        </div>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(4,1fr)", gap: 10 }}>
        {[
          { label: "Total",        val: kpis.total,           color: "#3A3C7A", sub: "proveedores" },
          { label: "Con crédito",  val: kpis.conCredito,      color: "#1D9E75", sub: "ofrecen días" },
          { label: "Calif. prom.", val: `⭐ ${kpis.calProm}`, color: "#F59E0B", sub: "sobre 5" },
          { label: "Top ★★★★★",   val: kpis.estrellas5,      color: "#F59E0B", sub: "5 estrellas" },
        ].map(k => (
          <div key={k.label} className="kpi-card card-lift" style={{ background: "#fff", border: "0.5px solid #e8ecf3", borderTop: `3px solid ${k.color}`, borderRadius: 12, padding: "14px 16px" }}>
            <div style={{ fontSize: 10, color: "#64748b", marginBottom: 4, fontWeight: 500 }}>{k.label}</div>
            <div style={{ fontSize: 22, fontWeight: 800, color: "#1e293b", letterSpacing: "-0.5px" }}>{k.val}</div>
            <div style={{ fontSize: 10, color: "#94a3b8", marginTop: 3, fontWeight: 500 }}>{k.sub}</div>
          </div>
        ))}
      </div>

      {mostrar && (
        <FormProveedor
          form={form}
          setForm={setForm}
          onSubmit={handleSubmit}
          onCancel={() => { setMostrar(false); setEditId(null); setForm(INICIAL); }}
          editId={editId}
        />
      )}

      <div style={{ background: "#fff", border: "0.5px solid #e2e8f0", borderRadius: 12, padding: "14px 16px", display: "flex", flexDirection: "column", gap: 10 }}>
        <input
          type="text"
          placeholder="🔍  Buscar por nombre, RUC, ciudad, categoría, contacto, zona o notas..."
          value={busqueda}
          onChange={e => setBusqueda(e.target.value)}
          style={{ width: "100%", padding: "10px 14px", border: "1.5px solid #e2e8f0", borderRadius: 10, fontSize: 13, outline: "none", boxSizing: "border-box", fontFamily: "inherit" }}
        />

        <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
          {catsEnUso.length > 0 && (
            <select
              value=""
              onChange={e => { if (e.target.value) toggleFiltroCat(e.target.value); }}
              style={{ padding: "7px 10px", border: "1px solid #e2e8f0", borderRadius: 8, fontSize: 12, color: "#475569", background: "#fff", cursor: "pointer" }}
            >
              <option value="">Categoría: {filtroCats.length === 0 ? "Todas" : "+"}</option>
              {catsEnUso.filter(c => !filtroCats.includes(c)).map(c => (
                <option key={c} value={c}>{c}</option>
              ))}
            </select>
          )}

          {["Todos","5","4","3"].map(v => (
            <button key={v} onClick={() => setFiltroCal(v)} style={{
              padding: "6px 12px", borderRadius: 8, fontSize: 11, fontWeight: 600, border: "none", cursor: "pointer",
              background: filtroCal === v ? "#F59E0B" : "#f1f5f9",
              color:      filtroCal === v ? "#fff"    : "#64748b",
            }}>
              {v === "Todos" ? "Todas" : `${v} ${"★".repeat(Number(v))}`}
            </button>
          ))}

          {filtroCats.map(cat => (
            <span key={cat} style={{ display: "inline-flex", alignItems: "center", gap: 5, fontSize: 11, fontWeight: 600, padding: "5px 10px", borderRadius: 6, background: "#EEF2FF", color: "#3A3C7A" }}>
              {cat}
              <span onClick={() => toggleFiltroCat(cat)} style={{ cursor: "pointer", color: "#6366f1", fontSize: 14, fontWeight: 700, lineHeight: 1 }}>×</span>
            </span>
          ))}

          {hayFiltros && (
            <span
              onClick={() => { setBusqueda(""); setFiltroCats([]); setFiltroCal("Todos"); }}
              style={{ padding: "5px 10px", borderRadius: 8, fontSize: 11, color: "#DC2626", background: "#FCEBEB", cursor: "pointer", fontWeight: 600, marginLeft: "auto" }}
            >
              × Limpiar filtros
            </span>
          )}
        </div>
      </div>

      {filtrados.length === 0 ? (
        <div style={{ textAlign: "center", padding: "48px 20px", background: "#fff", borderRadius: 12, border: "0.5px solid #e2e8f0", color: "#94a3b8", fontSize: 13 }}>
          {proveedores.length === 0 ? "Sin proveedores. Agrega el primero con el botón de arriba." : "No se encontraron proveedores con este filtro."}
        </div>
      ) : (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(300px, 1fr))", gap: 12 }}>
          {filtrados.map((p, i) => (
            <div key={p.id} className="stagger-item" style={{ animationDelay: `${Math.min(i * 40, 320)}ms` }}>
              <ProveedorCard
                p={p}
                onEditar={editar}
                onEliminar={eliminar}
                onVer360={setPerfil360}
              />
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
